import * as adminApi from '../../services/adminApi.js';

export const createAdminReportsSlice = (set, get) => ({
  // Reports state
  reports: {
    sales: {
      data: [],
      totals: { revenue: 0, count: 0, avgTransaction: 0 },
      loading: false,
      error: null,
      dateRange: '30d',
      startDate: null,
      endDate: null
    },
    expenses: {
      data: [],
      summary: { total: 0, byCategory: {}, count: 0 },
      loading: false,
      error: null,
      dateRange: '30d',
      startDate: null,
      endDate: null
    },
    inventory: {
      data: [],
      summary: { total: 0, lowStock: 0, outOfStock: 0 },
      loading: false,
      error: null
    },
    metrics: {
      data: [],
      loading: false,
      error: null,
      dateRange: '30d'
    }
  },

  // ====== REPORTS ACTIONS ======

  /**
   * Load sales report
   * @param {string} dateRange - Date range: 'all', '7d', or '30d'
   * @param {number|null} branchId - Optional branch ID filter
   */
  loadSalesReport: async ({ dateRange = '30d', branchId = null, startDate = null, endDate = null } = {}) => {
    set((state) => ({
      admin: {
        ...state.admin,
        reports: {
          ...state.admin.reports,
          sales: {
            ...state.admin.reports.sales,
            loading: true,
            error: null,
            dateRange,
            startDate,
            endDate
          }
        }
      }
    }));

    try {
      const data = await adminApi.fetchSalesReport({ dateRange, branchId, startDate, endDate });
      set((state) => ({
        admin: {
          ...state.admin,
          reports: {
            ...state.admin.reports,
            sales: {
              data: data.sales || [],
              totals: data.totals || { revenue: 0, count: 0, avgTransaction: 0 },
              loading: false,
              error: null,
              dateRange,
              startDate,
              endDate
            }
          }
        }
      }));
    } catch (error) {
      set((state) => ({
        admin: {
          ...state.admin,
          reports: {
            ...state.admin.reports,
            sales: {
              ...state.admin.reports.sales,
              loading: false,
              error: error.message || 'Failed to load sales report'
            }
          }
        }
      }));
    }
  },

  /**
   * Load expense report
   * @param {string} dateRange - Date range: 'all', '7d', or '30d'
   * @param {number|null} branchId - Optional branch ID filter
   */
  loadExpenseReport: async ({ dateRange = '30d', branchId = null, startDate = null, endDate = null } = {}) => {
    set((state) => ({
      admin: {
        ...state.admin,
        reports: {
          ...state.admin.reports,
          expenses: {
            ...state.admin.reports.expenses,
            loading: true,
            error: null,
            dateRange,
            startDate,
            endDate
          }
        }
      }
    }));

    try {
      const data = await adminApi.fetchExpenseReport({ dateRange, branchId, startDate, endDate });
      set((state) => ({
        admin: {
          ...state.admin,
          reports: {
            ...state.admin.reports,
            expenses: {
              data: data.expenses || [],
              summary: data.summary || { total: 0, byCategory: {}, count: 0 },
              loading: false,
              error: null,
              dateRange,
              startDate,
              endDate
            }
          }
        }
      }));
    } catch (error) {
      set((state) => ({
        admin: {
          ...state.admin,
          reports: {
            ...state.admin.reports,
            expenses: {
              ...state.admin.reports.expenses,
              loading: false,
              error: error.message || 'Failed to load expense report'
            }
          }
        }
      }));
    }
  },

  /**
   * Load inventory report
   */
  loadInventoryReport: async () => {
    set((state) => ({
      admin: {
        ...state.admin,
        reports: {
          ...state.admin.reports,
          inventory: {
            ...state.admin.reports.inventory,
            loading: true,
            error: null
          }
        }
      }
    }));

    try {
      const data = await adminApi.fetchInventoryReport();
      set((state) => ({
        admin: {
          ...state.admin,
          reports: {
            ...state.admin.reports,
            inventory: {
              data: data.inventory || [],
              summary: data.summary || { total: 0, lowStock: 0, outOfStock: 0 },
              loading: false,
              error: null
            }
          }
        }
      }));
    } catch (error) {
      set((state) => ({
        admin: {
          ...state.admin,
          reports: {
            ...state.admin.reports,
            inventory: {
              ...state.admin.reports.inventory,
              loading: false,
              error: error.message || 'Failed to load inventory report'
            }
          }
        }
      }));
    }
  },

  /**
   * Load branch metrics
   * @param {string} dateRange - Date range: 'all', '7d', or '30d'
   */
  loadBranchMetrics: async (dateRange = '30d') => {
    set((state) => ({
      admin: {
        ...state.admin,
        reports: {
          ...state.admin.reports,
          metrics: {
            ...state.admin.reports.metrics,
            loading: true,
            error: null,
            dateRange
          }
        }
      }
    }));

    try {
      const data = await adminApi.fetchBranchMetrics(dateRange);
      set((state) => ({
        admin: {
          ...state.admin,
          reports: {
            ...state.admin.reports,
            metrics: {
              data: data.metrics || [],
              loading: false,
              error: null,
              dateRange
            }
          }
        }
      }));
    } catch (error) {
      set((state) => ({
        admin: {
          ...state.admin,
          reports: {
            ...state.admin.reports,
            metrics: {
              ...state.admin.reports.metrics,
              loading: false,
              error: error.message || 'Failed to load branch metrics'
            }
          }
        }
      }));
    }
  },

  /**
   * Clear all report errors
   */
  clearReportErrors: () => {
    set((state) => ({
      admin: {
        ...state.admin,
        reports: {
          sales: { ...state.admin.reports.sales, error: null },
          expenses: { ...state.admin.reports.expenses, error: null },
          inventory: { ...state.admin.reports.inventory, error: null },
          metrics: { ...state.admin.reports.metrics, error: null }
        }
      }
    }));
  }
});